import { FC } from 'react'

import FormGroup from '../../../../core/components/FormGroup'
import { Button, Checkbox, Input } from '@/components/ui'

interface TaxItemProps {
  categoryIndex: number
  taxIndex: number
  register: any
  errors: any
  watch: any
  setValue: any
  hasEditPermission: boolean
  onRemove: () => void
  onVatChange: (
    categoryIndex: number,
    taxIndex: number,
    checked: boolean,
  ) => void
}

export const TaxItem: FC<TaxItemProps> = ({
  categoryIndex,
  taxIndex,
  register,
  errors,
  watch,
  setValue,
  hasEditPermission,
  onRemove,
  onVatChange,
}) => {
  const basePath = `categories.${categoryIndex}.taxes.${taxIndex}` as const
  const taxErrors = errors?.categories?.[categoryIndex]?.taxes?.[taxIndex]

  const isVat = watch(`${basePath}.vat`)
  const includeInInvoice = watch(`${basePath}.includeInInvoice`)
  const includedInTicketPrice = watch(`${basePath}.includedInTicketPrice`)

  return (
    <div className="rounded-2xl border border-monochrome-120 p-3 flex flex-col gap-3">
      <FormGroup label="TAX Name">
        <Input
          isInvalid={!!taxErrors?.name?.message}
          {...register(`${basePath}.name`)}
          placeholder="Enter Tax Name"
          disabled={!hasEditPermission}
        />
      </FormGroup>
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <FormGroup
          label="TAX in %"
          tooltip="Enter the tax percentage that will be applied"
        >
          <Input
            isInvalid={!!taxErrors?.amountPercentage?.message}
            {...register(`${basePath}.amountPercentage`, {
              valueAsNumber: true,
            })}
            type="number"
            placeholder="Enter percentage"
            disabled={!hasEditPermission}
          />
        </FormGroup>
        <span className="pb-2 text-monochrome-2">or</span>
        <FormGroup
          label="Fixed TAX in EUR (Default Settings)"
          tooltip="Enter a fixed tax amount in the default currency"
        >
          <Input
            isInvalid={!!taxErrors?.amountFixed?.message}
            {...register(`${basePath}.amountFixed`, {
              valueAsNumber: true,
            })}
            type="number"
            placeholder="Enter amount"
            disabled={!hasEditPermission}
          />
        </FormGroup>
      </div>
      <div className="flex flex-col gap-2">
        <FormGroup
          label="VAT"
          direction="row"
          labelClassName="whitespace-nowrap"
          inputWrapperClassName="w-auto"
          wrapperClassName="w-auto flex-row-reverse justify-end"
          labelProps={{
            htmlFor: `${basePath}.vat`,
          }}
        >
          <Checkbox
            id={`${basePath}.vat`}
            checked={isVat}
            onCheckedChange={(checked) =>
              onVatChange(categoryIndex, taxIndex, checked === true)
            }
            disabled={!hasEditPermission}
          />
        </FormGroup>
        <FormGroup
          label="Include in Invoice"
          direction="row"
          labelClassName="whitespace-nowrap"
          inputWrapperClassName="w-auto"
          wrapperClassName="w-auto flex-row-reverse justify-end"
          labelProps={{
            htmlFor: `${basePath}.includeInInvoice`,
          }}
        >
          <Checkbox
            id={`${basePath}.includeInInvoice`}
            checked={includeInInvoice}
            onCheckedChange={(checked) =>
              setValue(`${basePath}.includeInInvoice`, checked === true)
            }
            disabled={!hasEditPermission}
          />
        </FormGroup>
        <FormGroup
          label="Included in Ticket Price"
          direction="row"
          labelClassName="whitespace-nowrap"
          inputWrapperClassName="w-auto"
          wrapperClassName="w-auto flex-row-reverse justify-end"
          labelProps={{
            htmlFor: `${basePath}.includedInTicketPrice`,
          }}
        >
          <Checkbox
            id={`${basePath}.includedInTicketPrice`}
            checked={includedInTicketPrice}
            onCheckedChange={(checked) =>
              setValue(`${basePath}.includedInTicketPrice`, checked === true)
            }
            disabled={!hasEditPermission}
          />
        </FormGroup>
      </div>
      <div className="flex justify-between items-center">
        <Button
          onClick={onRemove}
          variant="destructive"
          type="button"
          disabled={!hasEditPermission}
        >
          Delete
        </Button>
      </div>
    </div>
  )
}
